import { qrcodegen } from './vendor/qrcodegen';

export type QrErrorCorrection = 'L' | 'M' | 'Q' | 'H';
export type QrModuleStyle = 'square' | 'rounded' | 'dots' | 'diamond' | 'horizontal' | 'vertical';
export type QrFinderStyle = 'square' | 'rounded' | 'circle';
export type QrFrameStyle = 'none' | 'scan';
export type QrBackgroundFit = 'cover' | 'contain';

export interface QrRenderOptions {
  errorCorrection: QrErrorCorrection;
  darkColor: string;
  lightColor: string;
  accentColor?: string;
  margin: number;
  pixelSize: number;
  moduleStyle?: QrModuleStyle;
  finderStyle?: QrFinderStyle;
  frameStyle?: QrFrameStyle;
  caption?: string;
  logoDataUrl?: string;
  logoScale?: number;
  backgroundImageDataUrl?: string;
  backgroundImage?: HTMLImageElement;
  backgroundFit?: QrBackgroundFit;
  backgroundOpacity?: number;
  qrScale?: number;
}

export interface QrSymbol {
  size: number;
  version: number;
  modules: readonly (readonly boolean[])[];
}

export const QR_MAX_INPUT_BYTES = 1200;

const ECC = {
  L: qrcodegen.QrCode.Ecc.LOW,
  M: qrcodegen.QrCode.Ecc.MEDIUM,
  Q: qrcodegen.QrCode.Ecc.QUARTILE,
  H: qrcodegen.QrCode.Ecc.HIGH,
} as const;

export function createQrSymbol(text: string, errorCorrection: QrErrorCorrection): QrSymbol {
  if (!text.trim()) throw new Error('QR content cannot be empty.');
  const bytes = new TextEncoder().encode(text).byteLength;
  if (bytes > QR_MAX_INPUT_BYTES) throw new Error(`QR content exceeds the ${QR_MAX_INPUT_BYTES}-byte safety limit.`);
  const qr = qrcodegen.QrCode.encodeText(text, ECC[errorCorrection]);
  return {
    size: qr.size,
    version: (qr.size - 17) / 4,
    modules: Array.from({ length: qr.size }, (_, y) =>
      Array.from({ length: qr.size }, (_, x) => qr.getModule(x, y))),
  };
}

export function makeQrSvg(text: string, options: QrRenderOptions): string {
  assertOptions(options);
  const symbol = createQrSymbol(text, options.errorCorrection);
  const style = normalizedStyle(options);
  const layout = renderLayout(symbol, options);
  const modules: string[] = [];
  for (let y = 0; y < symbol.size; y += 1) {
    for (let x = 0; x < symbol.size; x += 1) {
      if (!symbol.modules[y]?.[x] || isFinderCell(symbol.size, x, y)) continue;
      modules.push(svgModule(layout.symbolX + x, layout.symbolY + y, style.moduleStyle, options.darkColor));
    }
  }
  const background = options.backgroundImageDataUrl
    ? svgArtworkBackground(layout, options, style)
    : style.frameStyle === 'scan'
      ? `<rect width="100%" height="100%" rx="1.4" fill="${style.accentColor}"/><rect x="${layout.qrX}" y="${layout.qrY}" width="${layout.qrSize}" height="${layout.qrSize}" rx=".8" fill="${options.lightColor}"/>${svgCaption(layout, style.caption, options.lightColor)}`
      : `<rect width="100%" height="100%" fill="${options.lightColor}"/>`;
  const finders = finderOrigins(symbol.size).map(([x, y]) => svgFinder(layout.symbolX + x, layout.symbolY + y, style.finderStyle, style.accentColor, options.lightColor)).join('');
  const logo = options.logoDataUrl ? svgLogo(options.logoDataUrl, options.logoScale ?? 0.16, layout, options.lightColor) : '';
  const height = Math.round(options.pixelSize * layout.height / layout.width);
  const rendering = style.moduleStyle === 'square' && style.finderStyle === 'square' ? 'crispEdges' : 'geometricPrecision';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${options.pixelSize}" height="${height}" viewBox="0 0 ${layout.width} ${layout.height}" shape-rendering="${rendering}">${background}${modules.join('')}${finders}${logo}</svg>`;
}

export function drawQrToCanvas(canvas: HTMLCanvasElement, text: string, options: QrRenderOptions): QrSymbol {
  assertOptions(options);
  const symbol = createQrSymbol(text, options.errorCorrection);
  const style = normalizedStyle(options);
  const layout = renderLayout(symbol, options);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is unavailable in this browser.');
  canvas.width = options.pixelSize;
  canvas.height = Math.round(options.pixelSize * layout.height / layout.width);
  const unit = canvas.width / layout.width;
  context.imageSmoothingEnabled = style.moduleStyle !== 'square' || style.finderStyle !== 'square';
  context.fillStyle = options.backgroundImage ? options.lightColor : style.frameStyle === 'scan' ? style.accentColor : options.lightColor;
  context.fillRect(0, 0, canvas.width, canvas.height);
  if (options.backgroundImage) {
    context.save();
    context.globalAlpha = options.backgroundOpacity ?? 0.92;
    drawCoverImage(context, options.backgroundImage, canvas.width, canvas.height, options.backgroundFit ?? 'cover');
    context.restore();
    drawArtworkPanel(context, layout, unit, options.lightColor);
    if (style.frameStyle === 'scan') drawArtworkCaption(context, layout, unit, style.caption, style.accentColor, options.lightColor);
  } else if (style.frameStyle === 'scan') {
    context.fillStyle = options.lightColor;
    roundedRect(context, layout.qrX * unit, layout.qrY * unit, layout.qrSize * unit, layout.qrSize * unit, 0.8 * unit);
    context.fill();
    drawCaption(context, layout, unit, style.caption, options.lightColor);
  }
  for (let y = 0; y < symbol.size; y += 1) {
    for (let x = 0; x < symbol.size; x += 1) {
      if (!symbol.modules[y]?.[x] || isFinderCell(symbol.size, x, y)) continue;
      drawCanvasModule(context, (layout.symbolX + x) * unit, (layout.symbolY + y) * unit, unit, style.moduleStyle, options.darkColor);
    }
  }
  for (const [x, y] of finderOrigins(symbol.size)) drawCanvasFinder(context, (layout.symbolX + x) * unit, (layout.symbolY + y) * unit, unit, style.finderStyle, style.accentColor, options.lightColor);
  return symbol;
}

export function drawQrLogoToCanvas(canvas: HTMLCanvasElement, image: HTMLImageElement, text: string, options: QrRenderOptions): void {
  assertOptions(options);
  if (!options.logoDataUrl) return;
  const symbol = createQrSymbol(text, options.errorCorrection);
  const layout = renderLayout(symbol, options);
  const context = canvas.getContext('2d');
  if (!context) return;
  const unit = canvas.width / layout.width;
  const size = layout.qrSize * unit * (options.logoScale ?? 0.16);
  const padding = layout.qrSize * unit * 0.018;
  const centerX = (layout.qrX + layout.qrSize / 2) * unit;
  const centerY = (layout.qrY + layout.qrSize / 2) * unit;
  const left = centerX - size / 2;
  const top = centerY - size / 2;
  context.fillStyle = options.lightColor;
  roundedRect(context, left - padding, top - padding, size + padding * 2, size + padding * 2, Math.max(4, padding));
  context.fill();
  const ratio = Math.min(size / image.naturalWidth, size / image.naturalHeight);
  const width = image.naturalWidth * ratio;
  const height = image.naturalHeight * ratio;
  context.drawImage(image, centerX - width / 2, centerY - height / 2, width, height);
}

export function assertQrColors(darkColor: string, lightColor: string): void {
  const dark = relativeLuminance(parseHex(darkColor));
  const light = relativeLuminance(parseHex(lightColor));
  const contrast = (Math.max(dark, light) + 0.05) / (Math.min(dark, light) + 0.05);
  if (dark >= light || contrast < 4.5) throw new Error('Use a dark foreground and a light background with at least 4.5:1 contrast.');
}

export async function runQrCodeFactoryAction(text: string): Promise<{ output: string; summary: string }> {
  const options: QrRenderOptions = { errorCorrection: 'M', darkColor: '#111111', lightColor: '#ffffff', margin: 4, pixelSize: 512 };
  const symbol = createQrSymbol(text, options.errorCorrection);
  return { output: makeQrSvg(text, options), summary: `QR Code version ${symbol.version} · ${symbol.size} × ${symbol.size} modules` };
}

function assertOptions(options: QrRenderOptions): void {
  if (!Object.hasOwn(ECC, options.errorCorrection)) throw new Error('Unsupported QR error correction level.');
  if (!Number.isInteger(options.margin) || options.margin < 4 || options.margin > 12) throw new Error('QR quiet zone must be 4–12 modules.');
  if (!Number.isInteger(options.pixelSize) || options.pixelSize < 256 || options.pixelSize > 2048) throw new Error('QR image size must be 256–2048 pixels.');
  assertQrColors(options.darkColor, options.lightColor);
  if (options.accentColor) assertQrColors(options.accentColor, options.lightColor);
  if (options.moduleStyle && !['square', 'rounded', 'dots', 'diamond', 'horizontal', 'vertical'].includes(options.moduleStyle)) throw new Error('Unsupported QR module style.');
  if (options.finderStyle && !['square', 'rounded', 'circle'].includes(options.finderStyle)) throw new Error('Unsupported QR finder style.');
  if (options.frameStyle && !['none', 'scan'].includes(options.frameStyle)) throw new Error('Unsupported QR frame style.');
  if ((options.caption ?? '').length > 48 || /[\u0000-\u001f\u007f]/u.test(options.caption ?? '')) throw new Error('QR caption must be 48 printable characters or fewer.');
  if (options.logoDataUrl) {
    if (!/^data:image\/(?:png|jpeg|webp);base64,[a-z0-9+/=]+$/iu.test(options.logoDataUrl) || options.logoDataUrl.length > 3_000_000) throw new Error('Logo must be a local PNG, JPEG or WebP image under 2 MB.');
    if (options.errorCorrection !== 'H') throw new Error('Logo QR codes require high error correction.');
    if ((options.logoScale ?? 0) < 0.12 || (options.logoScale ?? 0) > 0.2) throw new Error('Logo size must be 12–20% of the QR code.');
  }
  if (options.backgroundImageDataUrl && (!/^data:image\/(?:png|jpeg|webp);base64,[a-z0-9+/=]+$/iu.test(options.backgroundImageDataUrl) || options.backgroundImageDataUrl.length > 7_500_000)) {
    throw new Error('QR background must be a PNG, JPEG or WebP image under 5 MB.');
  }
  if (options.backgroundFit && !['cover', 'contain'].includes(options.backgroundFit)) throw new Error('Unsupported QR background fit.');
  if (options.backgroundOpacity !== undefined && (options.backgroundOpacity < 0.35 || options.backgroundOpacity > 1)) throw new Error('QR background opacity must be 35–100%.');
  if (options.qrScale !== undefined && (options.qrScale < 0.58 || options.qrScale > 0.84)) throw new Error('QR artwork scale must be 58–84%.');
  if ((options.backgroundImage || options.backgroundImageDataUrl) && (options.errorCorrection !== 'H' || options.margin < 6)) {
    throw new Error('QR backgrounds require high error correction and at least a 6-module quiet zone.');
  }
}

function svgLogo(dataUrl: string, scale: number, layout: QrLayout, background: string): string {
  const logoSize = layout.qrSize * scale;
  const padding = layout.qrSize * 0.018;
  const x = layout.qrX + (layout.qrSize - logoSize) / 2;
  const y = layout.qrY + (layout.qrSize - logoSize) / 2;
  return `<rect x="${x - padding}" y="${y - padding}" width="${logoSize + padding * 2}" height="${logoSize + padding * 2}" rx="${padding}" fill="${background}"/><image href="${dataUrl}" x="${x}" y="${y}" width="${logoSize}" height="${logoSize}" preserveAspectRatio="xMidYMid meet"/>`;
}

type NormalizedStyle = {
  moduleStyle: QrModuleStyle;
  finderStyle: QrFinderStyle;
  frameStyle: QrFrameStyle;
  accentColor: string;
  caption: string;
};

type QrLayout = {
  width: number;
  height: number;
  qrX: number;
  qrY: number;
  qrSize: number;
  symbolX: number;
  symbolY: number;
  captionY: number;
};

function normalizedStyle(options: QrRenderOptions): NormalizedStyle {
  return {
    moduleStyle: options.moduleStyle ?? 'square',
    finderStyle: options.finderStyle ?? 'square',
    frameStyle: options.frameStyle ?? 'none',
    accentColor: options.accentColor ?? options.darkColor,
    caption: options.caption?.trim() || 'SCAN ME',
  };
}

function renderLayout(symbol: QrSymbol, options: QrRenderOptions): QrLayout {
  const qrSize = symbol.size + options.margin * 2;
  const artwork = Boolean(options.backgroundImage || options.backgroundImageDataUrl);
  if (artwork) {
    const width = qrSize / (options.qrScale ?? 0.7);
    const captionSpace = (options.frameStyle ?? 'none') === 'scan' ? width * 0.12 : 0;
    const qrY = Math.max(width * 0.055, (width - qrSize - captionSpace) / 2);
    return {
      width,
      height: width,
      qrX: (width - qrSize) / 2,
      qrY,
      qrSize,
      symbolX: (width - qrSize) / 2 + options.margin,
      symbolY: qrY + options.margin,
      captionY: qrY + qrSize + Math.max(captionSpace * 0.58, width * 0.045),
    };
  }
  const framed = (options.frameStyle ?? 'none') === 'scan';
  const framePadding = framed ? 2 : 0;
  const captionHeight = framed ? 5 : 0;
  return {
    width: qrSize + framePadding * 2,
    height: qrSize + framePadding * 2 + captionHeight,
    qrX: framePadding,
    qrY: framePadding,
    qrSize,
    symbolX: framePadding + options.margin,
    symbolY: framePadding + options.margin,
    captionY: qrSize + framePadding * 2 + captionHeight / 2,
  };
}

function isFinderCell(size: number, x: number, y: number): boolean {
  return (x < 7 && y < 7) || (x >= size - 7 && y < 7) || (x < 7 && y >= size - 7);
}

function finderOrigins(size: number): Array<readonly [number, number]> {
  return [[0, 0], [size - 7, 0], [0, size - 7]];
}

function svgModule(x: number, y: number, style: QrModuleStyle, color: string): string {
  if (style === 'dots') return `<circle cx="${x + 0.5}" cy="${y + 0.5}" r=".41" fill="${color}"/>`;
  if (style === 'diamond') return `<path d="M ${x + 0.5} ${y + 0.02} L ${x + 0.98} ${y + 0.5} L ${x + 0.5} ${y + 0.98} L ${x + 0.02} ${y + 0.5} Z" fill="${color}"/>`;
  if (style === 'horizontal') return `<rect x="${x}" y="${y + 0.17}" width="1" height=".66" rx=".33" fill="${color}"/>`;
  if (style === 'vertical') return `<rect x="${x + 0.17}" y="${y}" width=".66" height="1" rx=".33" fill="${color}"/>`;
  return `<rect x="${x}" y="${y}" width="1" height="1"${style === 'rounded' ? ' rx=".28"' : ''} fill="${color}"/>`;
}

function svgFinder(x: number, y: number, style: QrFinderStyle, color: string, background: string): string {
  if (style === 'circle') {
    return `<circle cx="${x + 3.5}" cy="${y + 3.5}" r="3.5" fill="${color}"/><circle cx="${x + 3.5}" cy="${y + 3.5}" r="2.5" fill="${background}"/><circle cx="${x + 3.5}" cy="${y + 3.5}" r="1.5" fill="${color}"/>`;
  }
  const outerRadius = style === 'rounded' ? '1.45' : '0';
  const innerRadius = style === 'rounded' ? '.9' : '0';
  const coreRadius = style === 'rounded' ? '.72' : '0';
  return `<rect x="${x}" y="${y}" width="7" height="7" rx="${outerRadius}" fill="${color}"/><rect x="${x + 1}" y="${y + 1}" width="5" height="5" rx="${innerRadius}" fill="${background}"/><rect x="${x + 2}" y="${y + 2}" width="3" height="3" rx="${coreRadius}" fill="${color}"/>`;
}

function svgCaption(layout: QrLayout, caption: string, color: string): string {
  return `<text x="${layout.width / 2}" y="${layout.captionY}" fill="${color}" text-anchor="middle" dominant-baseline="middle" font-family="Inter,Arial,sans-serif" font-size="2.15" font-weight="800" letter-spacing=".08">${escapeXml(caption)}</text>`;
}

function drawCanvasModule(context: CanvasRenderingContext2D, x: number, y: number, unit: number, style: QrModuleStyle, color: string): void {
  context.fillStyle = color;
  if (style === 'dots') {
    context.beginPath();
    context.arc(x + unit / 2, y + unit / 2, unit * 0.41, 0, Math.PI * 2);
    context.fill();
    return;
  }
  if (style === 'rounded') {
    roundedRect(context, x, y, unit, unit, unit * 0.28);
    context.fill();
    return;
  }
  if (style === 'diamond') {
    context.beginPath();
    context.moveTo(x + unit / 2, y + unit * 0.02);
    context.lineTo(x + unit * 0.98, y + unit / 2);
    context.lineTo(x + unit / 2, y + unit * 0.98);
    context.lineTo(x + unit * 0.02, y + unit / 2);
    context.closePath();
    context.fill();
    return;
  }
  if (style === 'horizontal' || style === 'vertical') {
    const horizontal = style === 'horizontal';
    roundedRect(context, x + (horizontal ? 0 : unit * 0.17), y + (horizontal ? unit * 0.17 : 0), horizontal ? unit : unit * 0.66, horizontal ? unit * 0.66 : unit, unit * 0.33);
    context.fill();
    return;
  }
  context.fillRect(Math.floor(x), Math.floor(y), Math.ceil(unit), Math.ceil(unit));
}

function drawCanvasFinder(context: CanvasRenderingContext2D, x: number, y: number, unit: number, style: QrFinderStyle, color: string, background: string): void {
  if (style === 'circle') {
    const centerX = x + unit * 3.5;
    const centerY = y + unit * 3.5;
    context.fillStyle = color;
    context.beginPath(); context.arc(centerX, centerY, unit * 3.5, 0, Math.PI * 2); context.fill();
    context.fillStyle = background;
    context.beginPath(); context.arc(centerX, centerY, unit * 2.5, 0, Math.PI * 2); context.fill();
    context.fillStyle = color;
    context.beginPath(); context.arc(centerX, centerY, unit * 1.5, 0, Math.PI * 2); context.fill();
    return;
  }
  context.fillStyle = color;
  roundedRect(context, x, y, unit * 7, unit * 7, style === 'rounded' ? unit * 1.45 : 0);
  context.fill();
  context.fillStyle = background;
  roundedRect(context, x + unit, y + unit, unit * 5, unit * 5, style === 'rounded' ? unit * 0.9 : 0);
  context.fill();
  context.fillStyle = color;
  roundedRect(context, x + unit * 2, y + unit * 2, unit * 3, unit * 3, style === 'rounded' ? unit * 0.72 : 0);
  context.fill();
}

function svgArtworkBackground(layout: QrLayout, options: QrRenderOptions, style: NormalizedStyle): string {
  const fit = options.backgroundFit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice';
  const panelPadding = Math.max(0.8, layout.width * 0.012);
  const panel = `<rect x="${layout.qrX - panelPadding}" y="${layout.qrY - panelPadding}" width="${layout.qrSize + panelPadding * 2}" height="${layout.qrSize + panelPadding * 2}" rx="${Math.max(0.8, layout.width * 0.025)}" fill="${options.lightColor}"/>`;
  const caption = style.frameStyle === 'scan' ? svgArtworkCaption(layout, style.caption, style.accentColor, options.lightColor) : '';
  return `<rect width="100%" height="100%" fill="${options.lightColor}"/><image href="${options.backgroundImageDataUrl}" width="${layout.width}" height="${layout.height}" preserveAspectRatio="${fit}" opacity="${options.backgroundOpacity ?? 0.92}"/>${panel}${caption}`;
}

function svgArtworkCaption(layout: QrLayout, caption: string, background: string, color: string): string {
  const width = Math.min(layout.width * 0.72, Math.max(layout.width * 0.38, caption.length * layout.width * 0.025));
  const height = layout.width * 0.085;
  return `<rect x="${(layout.width - width) / 2}" y="${layout.captionY - height / 2}" width="${width}" height="${height}" rx="${height / 2}" fill="${background}"/><text x="${layout.width / 2}" y="${layout.captionY}" fill="${color}" text-anchor="middle" dominant-baseline="middle" font-family="Inter,Arial,sans-serif" font-size="${layout.width * 0.034}" font-weight="800" letter-spacing=".06">${escapeXml(caption)}</text>`;
}

function drawArtworkPanel(context: CanvasRenderingContext2D, layout: QrLayout, unit: number, color: string): void {
  const padding = Math.max(0.8, layout.width * 0.012) * unit;
  context.fillStyle = color;
  context.shadowColor = 'rgba(0,0,0,.16)';
  context.shadowBlur = layout.width * unit * 0.028;
  context.shadowOffsetY = layout.width * unit * 0.008;
  roundedRect(context, layout.qrX * unit - padding, layout.qrY * unit - padding, layout.qrSize * unit + padding * 2, layout.qrSize * unit + padding * 2, layout.width * unit * 0.025);
  context.fill();
  context.shadowColor = 'transparent';
}

function drawArtworkCaption(context: CanvasRenderingContext2D, layout: QrLayout, unit: number, caption: string, background: string, color: string): void {
  const width = Math.min(layout.width * 0.72, Math.max(layout.width * 0.38, caption.length * layout.width * 0.025)) * unit;
  const height = layout.width * unit * 0.085;
  context.fillStyle = background;
  roundedRect(context, layout.width * unit / 2 - width / 2, layout.captionY * unit - height / 2, width, height, height / 2);
  context.fill();
  context.fillStyle = color;
  context.font = `800 ${layout.width * unit * 0.034}px Inter, Arial, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(caption, layout.width * unit / 2, layout.captionY * unit, width - height * 0.55);
}

function drawCoverImage(context: CanvasRenderingContext2D, image: HTMLImageElement, width: number, height: number, fit: QrBackgroundFit): void {
  const sourceWidth = image.naturalWidth;
  const sourceHeight = image.naturalHeight;
  if (!sourceWidth || !sourceHeight) return;
  const scale = fit === 'contain' ? Math.min(width / sourceWidth, height / sourceHeight) : Math.max(width / sourceWidth, height / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
}

function drawCaption(context: CanvasRenderingContext2D, layout: QrLayout, unit: number, caption: string, color: string): void {
  context.fillStyle = color;
  context.font = `800 ${Math.max(12, unit * 2.15)}px Inter, Arial, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(caption, layout.width * unit / 2, layout.captionY * unit, layout.width * unit - unit * 4);
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  context.beginPath();
  context.roundRect(x, y, width, height, Math.min(radius, width / 2, height / 2));
}

function escapeXml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&apos;');
}

function parseHex(value: string): readonly [number, number, number] {
  if (!/^#[0-9a-f]{6}$/iu.test(value)) throw new Error('QR colors must use six-digit hexadecimal values.');
  return [Number.parseInt(value.slice(1, 3), 16), Number.parseInt(value.slice(3, 5), 16), Number.parseInt(value.slice(5, 7), 16)];
}

function relativeLuminance(rgb: readonly [number, number, number]): number {
  const [red, green, blue] = rgb.map((value) => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red! + 0.7152 * green! + 0.0722 * blue!;
}
